/**
 * T8003 · Connectivity zustand-shaped store · module-level singleton.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §L1296 + §L1448 — owns `lastReconnectAt` for the realtime hook's
 * function-form `refetchOnMount` (T8007) AND drives the ConnectionStatus
 * pill UI.
 *
 * ZUSTAND-COMPATIBLE SHAPE
 * ------------------------
 * The store exposes a zustand-compatible surface (`getState`, `setState`,
 * `subscribe`, `useStore`) without taking a runtime dependency on the
 * `zustand` package. Selector-based subscriptions return stable references
 * when the selected slice does not change — the same contract zustand
 * provides via `useSyncExternalStoreWithSelector`.
 *
 * HMR SINGLETON
 * -------------
 * Pinned to `globalThis.__morfi_connectivity_store` so `vi.resetModules` /
 * Vite HMR reload do not double-instantiate it. `lastReconnectAt` survives
 * the reload — matches the long-lived-handle pin rule applied to every
 * singleton in the SDK (see `hmr-cleanup.ts`).
 */

import type { ConnectivityState, ConnTransition } from '../connectivity/fsm/state-machine.js';

export interface ConnectivityStoreState {
  state: ConnectivityState;
  cause: string | null;
  lastReconnectAt: number;
  lastTransitionAt: number;
  restRefetchExhaustedCount: number;
}

export interface ConnectivityStoreActions {
  applyTransition(t: ConnTransition): void;
  noteRestExhaustion(): void;
  resetRestExhaustion(): void;
}

export type ConnectivityStoreShape = ConnectivityStoreState & ConnectivityStoreActions;

export interface ConnectivityStoreApi {
  getState(): ConnectivityStoreShape;
  setState(
    updater:
      | Partial<ConnectivityStoreShape>
      | ((s: ConnectivityStoreShape) => Partial<ConnectivityStoreShape>),
  ): void;
  subscribe(
    listener: (s: ConnectivityStoreShape, prev: ConnectivityStoreShape) => void,
  ): () => void;
  /**
   * Selector-based subscription that returns a stable reference for an
   * unchanged slice. Mirrors the zustand `useStore(store, selector)`
   * pattern. Returns the most-recent selected slice.
   */
  select<T>(selector: (s: ConnectivityStoreShape) => T): T;
}

declare global {
  var __morfi_connectivity_store: ConnectivityStoreApi | undefined;
}

function createInitialState(): ConnectivityStoreState {
  return {
    state: 'CONNECTED',
    cause: null,
    lastReconnectAt: 0,
    lastTransitionAt: 0,
    restRefetchExhaustedCount: 0,
  };
}

function createConnectivityStore(): ConnectivityStoreApi {
  let state: ConnectivityStoreShape = {
    ...createInitialState(),
    applyTransition: (): void => {},
    noteRestExhaustion: (): void => {},
    resetRestExhaustion: (): void => {},
  };
  const listeners = new Set<(s: ConnectivityStoreShape, prev: ConnectivityStoreShape) => void>();

  function setState(
    updater:
      | Partial<ConnectivityStoreShape>
      | ((s: ConnectivityStoreShape) => Partial<ConnectivityStoreShape>),
  ): void {
    const prev = state;
    const patch = typeof updater === 'function' ? updater(prev) : updater;
    state = { ...prev, ...patch };
    for (const l of listeners) l(state, prev);
  }

  // Bind actions AFTER setState is defined so closures can call it.
  state = {
    ...state,
    applyTransition: (t: ConnTransition): void => {
      setState((s) => ({
        state: t.to as ConnectivityState,
        cause: t.cause ?? null,
        lastTransitionAt: t.settledAt,
        lastReconnectAt: t.to === 'CONNECTED' ? t.settledAt : s.lastReconnectAt,
      }));
    },
    noteRestExhaustion: (): void => {
      setState((s) => ({ restRefetchExhaustedCount: s.restRefetchExhaustedCount + 1 }));
    },
    resetRestExhaustion: (): void => {
      setState({ restRefetchExhaustedCount: 0 });
    },
  };

  function getState(): ConnectivityStoreShape {
    return state;
  }

  function subscribe(
    listener: (s: ConnectivityStoreShape, prev: ConnectivityStoreShape) => void,
  ): () => void {
    listeners.add(listener);
    return (): void => {
      listeners.delete(listener);
    };
  }

  function select<T>(selector: (s: ConnectivityStoreShape) => T): T {
    return selector(state);
  }

  return { getState, setState, subscribe, select };
}

/**
 * HMR-safe singleton. Survives `vi.resetModules()` / Vite HMR reload.
 * Subsequent imports return the SAME store reference, so `lastReconnectAt`
 * set pre-reload is still readable post-reload.
 */
export const connectivityStore: ConnectivityStoreApi =
  globalThis.__morfi_connectivity_store ??
  ((): ConnectivityStoreApi => {
    const s = createConnectivityStore();
    globalThis.__morfi_connectivity_store = s;
    return s;
  })();

/**
 * Test seam — wipes the singleton so each test starts with a fresh store.
 * Production code MUST NOT call this; it exists for vitest isolation.
 */
export function _resetConnectivityStoreForTest(): void {
  globalThis.__morfi_connectivity_store = undefined;
}
